import {asREM, InlineSvg, RsnSkeleton} from '@raison/ui-kit';
import {format} from 'date-fns/format';
import React from 'react';
import {NumericFormat} from 'react-number-format';
import {getOrderResultSign, TransactionSchema} from '~/entities/transactions-history';
import defaultIcon from '~/shared/legacy_res/img/svg/default.svg';
import arrow from '../imgs/arrow.svg';
import buy from '../imgs/buy.svg';
import clocks from '../imgs/clocks.svg';
import error from '../imgs/error.svg';
import exchange from '../imgs/exchange.svg';
import incomings from '../imgs/incomings.svg';
import outgoings from '../imgs/outgoings.svg';
import warning from '../imgs/warning.svg';
import styles from './TransactionItem.module.scss';

export interface TransactionItemProps {
    order: TransactionSchema;
    onClick: (() => void) | undefined;
    containerRef: React.RefCallback<Element> | undefined;
}

function TransactionItem({order, containerRef, onClick}: TransactionItemProps) {
    const groupIcon = (group: string) => {
        switch (group) {
            case 'buy':
                return buy;
            case 'incomings':
                return incomings;
            case 'unit_purchase':
            case 'exchange':
                return exchange;
            case 'outgoings':
                return outgoings;
            default:
                return defaultIcon;
        }
    };

    const getStatusClass = (order: TransactionSchema) => {
        const {status} = order;

        switch (status) {
            case 'completed':
                return order.group === 'outgoings' ? '' : styles.valueCompleted;
            case 'pending':
            case 'moderate':
                return styles.valueDefault;
            case 'rejected':
                return styles.valueRejected;
            default:
                return styles.valueDefault;
        }
    };

    const status = (order: TransactionSchema) => {
        switch (order.status) {
            case 'completed':
                return {
                    className: order.is_low_value ? styles.statusWarning : styles.statusCompleted,
                    src: order.is_low_value ? warning : arrow,
                };
            case 'pending':
            case 'moderate':
                return {className: styles.statusPending, src: clocks};
            case 'rejected':
                return {className: styles.statusRejected, src: error};
            default:
                return {className: '', src: ''};
        }
    };

    const typeIconSrc = groupIcon(order.group);
    const {src: statusIconSrc, className: statusIconClassName} = status(order);

    return (
        <div ref={containerRef} id={order.id.toString()} className={styles.container} onClick={onClick}>
            <img src={typeIconSrc} className={styles.icon} />
            <div className={styles.innerContainer}>
                {order.volume.value == null ? (
                    <span className={getStatusClass(order)}>{`-- ${order.volume.currency}`}</span>
                ) : (
                    <NumericFormat
                        prefix={getOrderResultSign(order)}
                        value={order.volume.value}
                        suffix={` ${order.volume.currency}`}
                        displayType='text'
                        className={getStatusClass(order)}
                        thousandSeparator
                        allowNegative={false}
                    />
                )}
                <div className={styles.descContainer}>
                    <div className={styles.titleContainer}>
                        <div>{order.type_human}</div>
                        {statusIconSrc && (
                            <div className={styles.statusIconContainer}>
                                <InlineSvg src={statusIconSrc} className={statusIconClassName} />
                            </div>
                        )}
                    </div>
                    <div className={styles.date}>{format(order.created_at, 'MMM dd, HH:mm')}</div>
                </div>
            </div>
        </div>
    );
}

TransactionItem.displayName = 'TransactionItem';

TransactionItem.Skeleton = function TransactionItemSkeleton({index = 1}: {index?: number}) {
    return (
        <div className='mb-4 flex w-full gap-2 last-of-type:mb-0' style={{opacity: 1 - index * 0.09}}>
            <RsnSkeleton circle width={36} height={36} />
            <div className='grid flex-1 gap-2'>
                <RsnSkeleton width={64} height={12} />
                <RsnSkeleton width={140} height={8} />
            </div>
            <div className='grid gap-2'>
                <div style={{height: asREM(12)}} />
                <RsnSkeleton width={64} height={8} />
            </div>
        </div>
    );
};

export default TransactionItem;
